/**
 * Timeline Page
 * Dedicated page for timeline visualization
 */

import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Home, Network, Database } from "lucide-react";
import { parseYears, buildPersonToItems } from "../utils/yearParsing";
import { useTeaCeremonyData } from "../hooks/useTeaCeremonyData";
import TimelineCanvas from "../components/TimelineCanvas";
import DetailsPanel from "../components/DetailsPanel";
import Legend from "../components/Legend";
import { transformPersonData } from "../utils/personDataProcessing";

const TimelinePage = () => {
  const { people, items, itemSlugs, isLoading, error } = useTeaCeremonyData();
  const [query, setQuery] = useState("");
  const [range, setRange] = useState({ min: null, max: null });
  const [focus, setFocus] = useState(null);
  const [showPeriods, setShowPeriods] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState('japan');
  const navigate = useNavigate();

  // Build person-to-items mapping from loaded data
  const personToItems = useMemo(() => buildPersonToItems(items), [items]);

  // Enrich people data with parsed years and items
  const enriched = useMemo(() => {
    return (people || []).map((p, idx) => {
      const yrs = parseYears(p.years);
      const linkedItems =
        personToItems[p.name_jp] || personToItems[p.name_romaji] || [];
      return { ...p, _idx: idx, yearsParsed: yrs, items: linkedItems };
    });
  }, [people, personToItems]);

  // Calculate timeline bounds
  const bounds = useMemo(() => {
    let min = Infinity, max = -Infinity;

    enriched.forEach((p) => {
      const { start, end } = p.yearsParsed || {};
      const a = start ?? end;
      const b = end ?? start;
      if (a != null) min = Math.min(min, a);
      if (b != null) max = Math.max(max, b);
    });

    if (!isFinite(min) || !isFinite(max)) {
      min = 1400;
      max = new Date().getFullYear();
    }

    return { min, max };
  }, [enriched]);

  // Scale function for timeline - compatible with TimelineCanvas expectations
  const scaleX = useMemo(() => {
    const domain = range.min == null ? bounds : range;
    const span = domain.max - domain.min;
    return (year, availableWidth) => {
      if (availableWidth) {
        // TimelineCanvas passes availableWidth
        return ((year - domain.min) / span) * availableWidth;
      } else {
        // Fallback for percentage-based scaling
        return ((year - domain.min) / span) * 100;
      }
    };
  }, [bounds, range]);

  // Filter and sort people
  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    const min = range.min ?? bounds.min;
    const max = range.max ?? bounds.max;

    return enriched
      .filter((p) => {
        const { start, end } = p.yearsParsed || {};
        const lo = start ?? end ?? -Infinity;
        const hi = end ?? start ?? Infinity;

        const within = (() => {
          if (lo === -Infinity && hi === Infinity) return false;
          return hi >= min && lo <= max;
        })();

        if (!within) return false;
        if (!q) return true;

        const hay = [
          p.name_jp,
          p.name_romaji,
          p.region,
          p.description,
          (p.items || []).join(" "),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      })
      .sort((a, b) => {
        const ay = a.yearsParsed.start ?? a.yearsParsed.end ?? 9999;
        const by = b.yearsParsed.start ?? b.yearsParsed.end ?? 9999;
        return ay - by;
      });
  }, [enriched, query, range, bounds]);

  // Handle double-click to navigate to person detail page
  const handlePersonDoubleClick = useCallback((person) => {
    if (person) {
      const personData = transformPersonData(person, items, itemSlugs);
      navigate(`/person/${personData.id}`);
    }
  }, [items, itemSlugs, navigate]);

  // Auto-set initial range to calculated bounds
  useEffect(() => {
    if (range.min == null) setRange(bounds);
  }, [bounds.min, bounds.max]);

  // Handle loading state
  if (isLoading) {
    return (
      <div className="p-6 max-w-[1200px] mx-auto">
        <div className="text-center py-12">
          <div className="text-lg font-semibold">Loading timeline data...</div>
          <div className="text-sm text-gray-600 mt-2">
            Please wait while we load the tea ceremony data.
          </div>
        </div>
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className="p-6 max-w-[1200px] mx-auto">
        <div className="text-center py-12">
          <div className="text-lg font-semibold text-red-600">
            Error loading data
          </div>
          <div className="text-sm text-gray-600 mt-2">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 max-w-[1200px] mx-auto">
      {/* Navigation */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <Link
            to="/"
            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-800 rounded-md hover:bg-gray-100"
          >
            <Home size={16} />
            Home
          </Link>
          <span className="text-gray-300">•</span>
          <h1 className="text-2xl font-semibold text-gray-800">Timeline View</h1>
        </div>

        <div className="flex items-center gap-2">
          <Link
            to="/network"
            className="flex items-center gap-2 px-3 py-2 text-sm bg-emerald-100 text-emerald-700 rounded-md hover:bg-emerald-200"
          >
            <Network size={16} />
            Network View
          </Link>
          <Link
            to="/editor"
            className="flex items-center gap-2 px-3 py-2 text-sm bg-purple-100 text-purple-700 rounded-md hover:bg-purple-200"
          >
            <Database size={16} />
            Edit Data
          </Link>
        </div>
      </div>

      <p className="text-sm text-gray-600 mb-4">
        Click any bar to view detailed information. Use search and year range controls to focus the view.
      </p>

      <div className="mb-4 grid md:grid-cols-4 gap-3 items-end">
        <div className="md:col-span-2">
          <label htmlFor="year-range" className="text-xs font-medium">
            Year Range
          </label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              className="w-32 border rounded-lg p-2"
              placeholder="Min year"
              value={range.min ?? ""}
              onChange={(e) =>
                setRange((r) => ({
                  ...r,
                  min: parseInt(e.target.value || "", 10),
                }))
              }
            />
            <span className="text-gray-500">to</span>
            <input
              type="number"
              className="w-32 border rounded-lg p-2"
              placeholder="Max year"
              value={range.max ?? ""}
              onChange={(e) =>
                setRange((r) => ({
                  ...r,
                  max: parseInt(e.target.value || "", 10),
                }))
              }
            />
            <button
              className="ml-2 px-3 py-2 rounded-lg border text-xs hover:bg-gray-50"
              onClick={() => setRange(bounds)}
            >
              Reset
            </button>
          </div>
        </div>

        <div>
          <label className="text-xs font-medium">Historical Periods</label>
          <div className="flex flex-col gap-1">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={showPeriods}
                onChange={(e) => setShowPeriods(e.target.checked)}
                className="rounded border-gray-300"
                style={{ color: '#1A80B4' }}
              />
              <span className="text-xs">Show periods</span>
            </label>
            {showPeriods && (
              <select
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value)}
                className="text-xs border rounded p-1"
              >
                <option value="japan">Japan</option>
                <option value="china">China</option>
              </select>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="search" className="text-xs font-medium">
            Search
          </label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2 top-2.5 text-gray-500" />
            <input
              id="search"
              className="w-full border rounded-lg pl-8 p-2"
              placeholder="name, region, item, school…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Timeline Container - full width */}
      <div className="w-full mb-4">
        <TimelineCanvas
          data={filtered}
          scaleX={scaleX}
          bounds={range.min == null ? bounds : range}
          onFocus={setFocus}
          onPersonDoubleClick={handlePersonDoubleClick}
          showPeriods={showPeriods}
          selectedCountry={selectedCountry}
        />

        <div className="mt-2 text-xs text-gray-500">
          Showing {filtered.length} / {people.length} people
        </div>
      </div>

      <DetailsPanel
        person={focus}
        onClose={() => setFocus(null)}
        personToItems={personToItems}
      />

      <Legend />
    </div>
  );
};

export default TimelinePage;